import type { DebtData } from '@/types';
import { applyCredit, debtPayable } from './accountNet';
import { isClearedDebt } from './debtFilters';
import { type ActiveDiscountOffer, type DiscountOptionKey, type DiscountOptionSnapshot, discountedAmountMillicents } from './discount-offers';

export interface DebtPrice {
  /** The amount, after any credit attributed to the debt. */
  amount: number;
  /** Whether a discount brought `amount` below the balance, so the balance is also struck through. */
  discounted: boolean;
}

export interface DebtHeadline extends DebtPrice {
  /** The best live discount, as a fraction, for the card's "up to X%" badge; null without one. */
  bestPct: number | null;
}

// A cleared, overpaid or arranged debt takes no discount, whatever the offer says: a plan's price
// was fixed when it was set up, and an offer arriving mid-plan would advertise a saving the
// instalments never see.
const discountable = (debt: DebtData) => !isClearedDebt(debt) && debt.currentBalance >= 0 && !debt.paymentPlan;

/**
 * What PaymentPlanTab prices a debt's plan at, and picks its tier on: a live plan discount off the
 * balance, then the debt's credit. `discounted` is the balance after the discount, null without one.
 * Pinned to the agent's per-debt plan length by accountPaymentParity.test.ts.
 */
export function planPrice(balance: number, creditApplied: number, offer: ActiveDiscountOffer | undefined): { discounted: number | null; amount: number } {
  const live = offer && offer.status !== 'expired' && offer.status !== 'superseded';
  const discountedMc = live ? discountedAmountMillicents(offer, 'payment_plan', Math.round(balance * 100_000)) : null;
  const discounted = discountedMc != null ? discountedMc / 100_000 : null;
  return { discounted, amount: applyCredit(discounted ?? balance, creditApplied) };
}

/**
 * What a debt costs under one discount option, priced as its payment tab prices it: the discount,
 * cap applied, against what is owed, then the credit. Without an option, its balance less credit.
 */
export function debtPrice(debt: DebtData, offer: ActiveDiscountOffer | undefined, option: DiscountOptionKey | null): DebtPrice {
  const currentMc = Math.round(debt.currentBalance * 100_000);
  const discountedMc = offer && option && discountable(debt) ? discountedAmountMillicents(offer, option, currentMc) : null;
  const priceMc = discountedMc != null && discountedMc < currentMc ? discountedMc : null;
  return { amount: priceMc != null ? applyCredit(priceMc / 100_000, debt.creditApplied ?? 0) : debtPayable(debt), discounted: priceMc != null };
}

/**
 * The amount a debt's card leads with: its price under the larger of the pay-in-full and plan
 * discounts, the one its badge advertises.
 */
export function debtHeadline(debt: DebtData, offer: ActiveDiscountOffer | undefined): DebtHeadline {
  const live = offer != null && offer.status !== 'expired' && offer.status !== 'superseded';
  const pct = (option: DiscountOptionSnapshot | undefined) => (live && option?.enabled ? option.percentage : null);
  const payNowPct = pct(offer?.configSnapshot.options?.pay_now);
  const planPct = pct(offer?.configSnapshot.options?.payment_plan);
  const bestOption = planPct != null && (payNowPct == null || planPct > payNowPct) ? 'payment_plan' : payNowPct != null ? 'pay_now' : null;
  return {
    bestPct: discountable(debt) && bestOption ? (bestOption === 'pay_now' ? payNowPct : planPct) : null,
    ...debtPrice(debt, offer, bestOption),
  };
}
